import { createFetch } from '@vueuse/core'
import { defineStore } from 'pinia'

import router from '@/router'
import i18n from '@/libs/i18n'
import { noteModuleRefusal } from '@/libs/license'
import { PrintError } from '@/libs/toast'
import { useAppStore } from '@/stores/app'

export const useFetchStore = defineStore('fetch', () => {
    const appStore = useAppStore()

    const fetch = createFetch({
        baseUrl: '/api/pms/v1',

        /**
         * NO CLIENT TIMEOUT, AND THAT IS DELIBERATE.
         *
         * `createFetch` takes one and this does not pass it, so nothing here abandons a
         * call the server is still answering. The service decides how long a request may
         * take — 60 s for an ordinary read, 5 min for one turn of the assistant
         * (`HTTP_API/Routes.go`, docs/AI.md §19) — and a browser that gave up first would
         * throw away an answer that was about to arrive, having already paid for it: a
         * model call is billed whether or not anybody reads the reply.
         *
         * What a person gets instead of a shorter wall is being told the wait is normal:
         * the conversation shows „Milo dela …" after five seconds
         * (`assistant.still_working`). If a call ever does need to be given up on, it is
         * given up on by the screen that made it, with an AbortSignal it owns.
         */
        options: {
            /**
             * KEEP THE BODY OF A FAILED CALL.
             *
             * VueUse throws it away by default, and this portal reads it everywhere:
             * every screen here calls `parseError(data.value, fallback)`, which without
             * this sees null on every refusal, matches no code, and prints its generic
             * fallback. A room refused because somebody else is standing in it (code 21)
             * and a room refused because it is out of service (code 32) both arrived as
             * "dejanja ni bilo mogoče izvesti", with the reason nowhere on the screen —
             * although `libs/error.ts` has had a sentence for each of them all along.
             *
             * Milo is what made it worth fixing rather than worth noting: a proposal the
             * server refuses puts the server's own reason ON THE CARD
             * (`AssistantConversation.decide`), and a card that said only "ni uspelo"
             * would invite the supervisor to press it again.
             *
             * The refusals still ARE errors: `error` is set and every `statusCode` check
             * in the portal is untouched — they all read the status first and the body
             * second. This only stops the reason from being discarded on the way.
             */
            updateDataOnError: true,

            beforeFetch({ options }) {
                options.headers = new Headers(options.headers)

                // Only when there IS one.
                //
                // This portal now has a screen with no session at all — the corridor
                // kiosk (docs/OSEBJE.md §8), which is a DEVICE and presents its own key
                // in `X-API-Key`. Setting the header unconditionally sent every one of
                // its calls a `Bearer ` with nothing after it: a credential that is not
                // one, beside the credential that is, and two things for the service's
                // door to compose. The screen portal does the same thing by not
                // injecting anything centrally at all (`pms-display/src/stores/fetch.ts`)
                // — which of the two applies is the caller's business.
                //
                // Nothing changes for a signed-in screen: an empty token was never worth
                // anything, so the header it produced was never doing any work.
                const token = appStore.getJWT()

                if (token) options.headers.set('Authorization', `Bearer ${token}`)

                return { options }
            },
            async onFetchError(ctx) {
                // The hotel has not bought this module. Noticed here, in the one place
                // every call passes through, rather than in each screen: whichever call
                // happens to be first — the round, the rooms, GET /me — is refused the
                // same way, and the portal has to stop rather than draw an empty round.
                //
                // Before the 401 branch and separate from it: this is a 403, nobody is
                // signed out for it, and there is nothing to sign in to.
                noteModuleRefusal(ctx.response?.status, ctx.data)

                // A refused sign-in is not an expired session. The login screen says
                // "wrong username or password" itself, and adding "your session has
                // expired" beside it tells somebody who mistyped a password that
                // something else went wrong too.
                if (ctx.response?.status === 401 && appStore.isLoggedIn()) {
                    // Say why. On a phone in a corridor a rejected session looks like
                    // the room refusing to start, and the housekeeper presses the button
                    // again instead of signing in.
                    PrintError(i18n.global.t('errors.session_expired'))

                    appStore.setLogout()
                    await router.push({ name: 'login' })
                }

                return ctx
            },
        },
    })

    return { fetch }
})
